<script>
import { mapGetters } from 'vuex';
import { useAlert, useTrack } from 'dashboard/composables';
import CsatMetrics from './components/CsatMetrics.vue';
import CsatTable from './components/CsatTable.vue';
import ReportFilterSelector from './components/FilterSelector.vue';
import { generateFileName } from '../../../../helper/downloadHelper';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ReportHeader from './components/ReportHeader.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

export default {
  name: 'CsatResponses',
  components: {
    CsatMetrics,
    CsatTable,
    ReportFilterSelector,
    ReportHeader,
    V4Button,
    PaginationFooter,
  },
  data() {
    return {
      pageIndex: 0,
      from: 0,
      to: 0,
      userIds: [],
      inbox: null,
      team: null,
      rating: null,
      // Remove TEMP/mockdata overrides for itemsPerPage and totalItems
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledOnAccount: 'accounts/isFeatureEnabledonAccount',
      metrics: 'csat/getMetrics',
    }),
    totalItems() {
      return this.metrics?.totalResponseCount || 0;
    },
    itemsPerPage() {
      return 25; // Use a constant, or dynamically from store/config if needed
    },
    requestPayload() {
      return {
        from: this.from,
        to: this.to,
        user_ids: this.userIds,
        inbox_id: this.inbox,
        team_id: this.team,
        rating: this.rating,
      };
    },
    isTeamsEnabled() {
      return this.isFeatureEnabledOnAccount(
        this.accountId,
        this.$options.FEATURE_FLAGS && this.$options.FEATURE_FLAGS.TEAM_MANAGEMENT
      );
    },
  },
  methods: {
    getAllData() {
      try {
        this.$store.dispatch('csat/getMetrics', this.requestPayload);
        this.getResponses();
      } catch {
        useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'), {type: 'error'});
      }
    },
    getResponses() {
      this.$store.dispatch('csat/get', {
        page: this.pageIndex + 1,
        ...this.requestPayload,
      });
    },
    downloadReports() {
      const type = 'csat';
      try {
        this.$store.dispatch('csat/downloadCSATReports', {
          fileName: generateFileName({ type, to: this.to }),
          ...this.requestPayload,
        });
      } catch (error) {
        useAlert(this.$t('REPORT.CSAT_REPORTS.DOWNLOAD_FAILED'), {type: 'error'});
      }
    },
    onPageNumberChange(pageIdx) {
      this.pageIndex = pageIdx;
      this.getResponses();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    onUpdateCurrentPage(pageNum) {
      this.pageIndex = pageNum - 1;
      this.getResponses();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    onFilterChange({
      from,
      to,
      selectedAgents,
      selectedInbox,
      selectedTeam,
      selectedRating,
    }) {
      this.from = from;
      this.to = to;
      this.userIds = selectedAgents.map(el => el.id);
      this.inbox = selectedInbox?.id;
      this.team = selectedTeam?.id;
      this.rating = selectedRating?.value;
      this.getAllData();
    },
  },
  FEATURE_FLAGS: {
    TEAM_MANAGEMENT: 'team_management',
  },
  mounted() {
    this.getAllData();
  },
};
</script>

<template>
  <div class="flex flex-col min-h-screen bg-n-background">
    <div class="flex-1 flex flex-col gap-6 pb-0">
      <ReportFilterSelector
        show-agents-filter
        show-inbox-filter
        show-rating-filter
        :show-team-filter="isTeamsEnabled"
        :show-business-hours-switch="false"
        @filter-change="onFilterChange"
        @download="downloadReports"
      />
      <CsatMetrics :filters="requestPayload" />
      <CsatTable :page-index="pageIndex" @page-change="onPageNumberChange" />
    </div>
    <footer class="max-w-[960px] mx-auto w-full px-4 pb-4">
      <PaginationFooter
        :current-page="pageIndex + 1"
        :total-items="totalItems"
        :items-per-page="itemsPerPage"
        @update:currentPage="onUpdateCurrentPage"
      />
    </footer>
  </div>
</template>
